import React from 'react';
import { Company, CompanyUser } from '@/types';
import { Users, Key, Clock, Edit2, Loader2, ShieldCheck, Trash2, ShieldAlert } from 'lucide-react';
import { Pagination } from '@shared/components/common/Pagination';

interface CompanyDashboardTeamTabProps {
  company: Company;
  isPrimaryAccount: boolean;
  teamMembers: CompanyUser[];
  teamPagination: {
    items: CompanyUser[];
    currentPage: number;
    totalCount: number;
  };
  isRefreshing: boolean;
  isResettingMemberId: string | null;
  teamPageSize: number;
  onOpenAddTeamMember: () => void;
  onEditMember: (member: CompanyUser) => void;
  onResetMemberPassword: (member: CompanyUser) => Promise<void>;
  onDeleteMember: (member: CompanyUser) => void;
  onTeamPageChange: (page: number) => void;
  onTeamPageSizeChange: (pageSize: number) => void;
}

export const CompanyDashboardTeamTab: React.FC<CompanyDashboardTeamTabProps> = ({
  company,
  isPrimaryAccount,
  teamMembers,
  teamPagination,
  isRefreshing,
  isResettingMemberId,
  teamPageSize,
  onOpenAddTeamMember,
  onEditMember,
  onResetMemberPassword,
  onDeleteMember,
  onTeamPageChange,
  onTeamPageSizeChange
}) => {
  return (
    <div className="space-y-6 animate-fade-in w-full">
      <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-purple-600" /> Dashboard Access Management
            </h3>
            <p className="text-slate-500 text-sm mt-1">
              Control who can log in to your company portal.
            </p>
          </div>
          {isPrimaryAccount ? (
            <button
              onClick={onOpenAddTeamMember}
              className="bg-purple-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-purple-700 flex items-center gap-2 shadow-lg shadow-purple-600/20"
            >
              <Users className="w-4 h-4" /> Add Team Member
            </button>
          ) : (
            <div className="text-[10px] font-bold text-slate-400 bg-slate-50 px-3 py-1 rounded-full border">
              Restricted: Only Primary Contact
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="bg-purple-100 p-2 rounded-full text-purple-600">
                <Key className="w-5 h-5" />
              </div>
              <div>
                <div className="font-bold text-slate-900">
                  {company.contactPerson}{' '}
                  <span className="ml-2 text-[10px] bg-purple-600 text-white px-2 py-0.5 rounded-full uppercase tracking-widest">
                    Primary
                  </span>
                </div>
                <div className="text-sm text-slate-500">{company.email}</div>
              </div>
            </div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-3">
              Master Account
            </div>
          </div>

          {teamMembers.length > 0 ? (
            teamPagination.items.map((member) => {
              const isResetting = isResettingMemberId === member.id;
              return (
                <div
                  key={member.id}
                  className="bg-white p-4 rounded-xl border border-slate-100 flex justify-between items-center group hover:border-purple-200 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-slate-100 p-2 rounded-full text-slate-500 group-hover:bg-purple-50 group-hover:text-purple-600 transition-colors">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-bold text-slate-800">{member.name}</div>
                      <div className="text-sm text-slate-500">{member.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {member.mustChangePassword && (
                      <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-1.5 rounded-full border border-amber-100 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Password Change Required
                      </span>
                    )}
                    {isPrimaryAccount && (
                      <button
                        onClick={() => onEditMember({ ...member })}
                        className="p-2 text-slate-300 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                        title="Edit Member"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                    )}
                    {isPrimaryAccount && (
                      <button
                        onClick={() => onResetMemberPassword(member)}
                        disabled={isResetting}
                        className="p-2 text-slate-300 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                        title="Send Reset Email"
                      >
                        {isResetting ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Key className="w-5 h-5" />
                        )}
                      </button>
                    )}
                    {isPrimaryAccount && (
                      <button
                        onClick={() => onDeleteMember(member)}
                        className="p-2 text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        title="Revoke Access"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-slate-100 rounded-2xl">
              <p className="text-slate-400 text-sm">No secondary team members added yet.</p>
            </div>
          )}

          {teamPagination.totalCount > 0 && (
            <Pagination
              page={teamPagination.currentPage}
              pageSize={teamPageSize}
              totalCount={teamPagination.totalCount}
              onPageChange={onTeamPageChange}
              onPageSizeChange={onTeamPageSizeChange}
              isLoading={isRefreshing}
            />
          )}
        </div>
        <div className="mt-12 bg-blue-50 p-6 rounded-2xl border border-blue-100">
          <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" /> Portal Usage Guidelines
          </h4>
          <ul className="text-sm text-blue-800 space-y-3">
            <li className="flex gap-2">
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 shrink-0" />
              Team members can see all bookings and health certificates for your company.
            </li>
            <li className="flex gap-2">
              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 shrink-0" />
              The Primary Account Holder is responsible for revoking access when a team member
              leaves.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
